import { useContext, useState } from "react";
import { SvgForward } from "ui/svgs";
import { AuthContext } from "context/AuthContext";
import { Tooltip, Table, InputNumber } from "antd";
import Switch from "react-switch";
import { showToast } from "ui/components/common";
import type { TableColumnsType } from "antd";
import { useAutomationService } from "pages/dashboard/automation/service";
import clsx from "clsx";
import useWsSender from "lib/wsSenderHook";
import { CameraInfo } from "lib/types";

export default function CameraAITable({
  cameras,
  setCameras,
  loading,
}: {
  cameras: CameraInfo[];
  setCameras: (arrs: CameraInfo[]) => void;
  loading: boolean;
}) {
  const { updateAdvancedAISetting } = useAutomationService();
  const { user } = useContext(AuthContext);
  const [wsRequestIds, setWsRequestIds] = useState(new Set());

  const { sendWsRequest } = useWsSender();

  const updateAISettingByField = async (
    i: number,
    field: string,
    value: any,
  ) => {
    const newCameras = [...cameras];
    newCameras[i].aiSettings = {
      ...newCameras[i].aiSettings,
      [field]: value,
    };

    setCameras(newCameras);
    const res = await updateAdvancedAISetting(
      {
        setting: newCameras[i]?.aiSettings,
      },
      +newCameras[i].id,
      +newCameras[i].orgId,
    );
    if (res.status !== 200) {
      showToast(1, "Failed to save advanced AI settings");
    }
  };

  const deviceForward = async (deviceType: string, deviceId: number) => {
    //start loading
    setWsRequestIds((state) => {
      state.add(deviceId);
      return new Set(state);
    });
    try {
      const res: any = await sendWsRequest(
        "device.remote_service.open",
        {
          deviceOpenRemoteService: {
            deviceId: deviceId.toString(),
            deviceType,
          },
        },
        { orgId: Number(user?.orgId) },
      );
      const query =
        "?expiredAt=" +
        res.deviceOpenRemoteServiceReply.cookie.expiredAt +
        "&name=" +
        res.deviceOpenRemoteServiceReply.cookie.name +
        "&value=" +
        res.deviceOpenRemoteServiceReply.cookie.value;
      window.open(
        "https://" + res.deviceOpenRemoteServiceReply.domain + query,
        "_blank",
      );
    } catch (e) {
      showToast(1, "There was a problem forwarding nvr. Error:" + e);
    } finally {
      //end loading
      setWsRequestIds((state) => {
        state.delete(deviceId);
        return new Set(state);
      });
    }
  };

  const columns: TableColumnsType<CameraInfo> = [
    {
      title: "Camera Name",
      dataIndex: "name",
    },
    {
      title: "Human Alerts",
      render: (e: CameraInfo, _, i) => (
        <div className="overflow-hidden break-words break-all font-semibold">
          <Switch
            onChange={(checked) => {
              updateAISettingByField(i, "enablePerson", checked);
            }}
            checked={e.aiSettings?.enablePerson}
            uncheckedIcon={false}
            checkedIcon={false}
            onColor="#3E83F6"
            offColor="#fff"
            handleDiameter={20}
            offHandleColor="#9E9E9E"
            className="border border-zinc-200"
            height={32}
            width={52}
          />
        </div>
      ),
    },
    {
      title: "Vehicle Alerts",
      render: (e: CameraInfo, _, i) => (
        <div className="overflow-hidden break-words break-all font-semibold">
          <Switch
            onChange={(checked) => {
              updateAISettingByField(i, "enableVehicle", checked);
            }}
            checked={e.aiSettings?.enableVehicle}
            uncheckedIcon={false}
            checkedIcon={false}
            onColor="#3E83F6"
            offColor="#fff"
            handleDiameter={20}
            offHandleColor="#9E9E9E"
            className="border border-zinc-200"
            height={32}
            width={52}
          />
        </div>
      ),
    },
    {
      title: "Rec. Limit",
      dataIndex: "recCredit",
    },
    {
      title: "Daily Limit",
      render: (e: CameraInfo, _, i) => {
        return (
          <InputNumber
            type="number"
            controls={false}
            value={e.aiSettings?.limitCredit}
            placeholder="Enter Daily Limit"
            disabled={true}
            onPressEnter={(e) => {
              updateAISettingByField(
                i,
                "limitCredit",
                (e.target as HTMLInputElement).value,
              );
            }}
            onBlur={(e) => {
              updateAISettingByField(
                i,
                "limitCredit",
                (e.target as HTMLInputElement).value,
              );
            }}
            style={{ width: "200px" }}
            className="border-none bg-[#F5F5F5] rounded-2xl"
          />
        );
      },
    },
    {
      title: "",
      render: (e: CameraInfo) => (
        <div className="overflow-hidden break-words break-all font-semibold">
          <Tooltip title="Camera Forward">
            <button
              className="hover:bg-neutral-200 rounded-full text-neutral-500 p-2 fill-neutral-500"
              disabled={wsRequestIds?.has(e.id)}
              onClick={(event) => {
                event.preventDefault();
                deviceForward("ip_camera", e.id);
              }}
            >
              {wsRequestIds?.has(e.id) ? (
                <div
                  className=" text-neutral-800  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                  role="status"
                ></div>
              ) : (
                <>
                  <SvgForward width="16" />
                </>
              )}
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="">
      <Table
        className="camera-ai-table w-full pr-2"
        loading={loading}
        //style={{ overflowX: "auto", maxHeight: "calc(100vh - 190px)" }}
        columns={columns}
        dataSource={cameras}
        pagination={false}
        rowClassName={() => "no-row-border"}
      />
    </div>
  );
}
